import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const checkboxVariants = cva(
  "ui-peer ui-shrink-0 ui-border ui-outline-none ui-transition-all ui-duration-200 ui-bg-white",
  {
    variants: {
      size: {
        sm: "ui-w-4 ui-h-4 ui-rounded-[4px]",
        md: "ui-w-5 ui-h-5 ui-rounded-md",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const switchTrackVariants = cva(
  "ui-block ui-h-5 ui-w-9 ui-rounded-full ui-transition-all",
  {
    variants: {
      checked: {
        true: "ui-bg-zinc-950",
        false: "",
      },
      disabled: {
        true: "ui-opacity-50",
        false: "ui-opacity-100",
      },
    },
    compoundVariants: [
      {
        checked: false,
        disabled: true,
        className: "ui-bg-gray-700",
      },
      {
        checked: false,
        disabled: false,
        className: "ui-bg-zinc-200",
      },
    ],
  }
);

const switchThumbVariants = cva(
  "ui-absolute ui-left-0.5 ui-top-0.5 ui-flex ui-h-4 ui-w-4 ui-items-center ui-justify-center ui-rounded-full ui-transition",
  {
    variants: {
      checked: {
        true: "ui-translate-x-full",
        false: "",
      },
      disabled: {
        true: "ui-bg-gray-700",
        false: "ui-bg-white",
      },
    },
  }
);

const CheckboxV2 = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  Omit<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, "type"> &
    VariantProps<typeof checkboxVariants> & {
      indeterminate?: boolean;
      type?: "checkbox" | "switch";
    }
>(({ className, indeterminate, checked, size, type = "checkbox", ...props }, ref) => {
  if (type === "switch") {
    const isChecked = checked === true || checked === "indeterminate";
    const isDisabled = props.disabled === true;

    return (
      <label className={cn("ui-flex ui-cursor-pointer ui-select-none ui-items-center", className)}>
        <div className="ui-relative">
          <input
            type="checkbox"
            disabled={isDisabled}
            checked={isChecked}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (props.onCheckedChange) {
                props.onCheckedChange(event.target.checked);
              }
            }}
            className="ui-sr-only"
            // Ensure ref is not passed directly to input if it's meant for Radix Root
            // But we can cast it if needed, though typically refs for switch/checkbox might differ
          />
          <div
            className={switchTrackVariants({
              checked: isChecked,
              disabled: isDisabled,
            })}
          ></div>
          <div
            className={switchThumbVariants({
              checked: isChecked,
              disabled: isDisabled,
            })}
          ></div>
        </div>
      </label>
    );
  }

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      checked={indeterminate ? "indeterminate" : checked}
      className={cn(
        checkboxVariants({ size }),
        // Border styles
        "ui-border-zinc-300 ui-border-1 dark:ui-border-zinc-700",

        // Focus state with custom shadow
        "focus-visible:ui-outline-none focus-visible:ui-shadow-[0_0_0_4px_rgba(71,96,255,0.15),0_1.5px_0.5px_-0.5px_rgba(0,0,0,0.04)]",

        // Checked state - dark background with white check
        "data-[state=checked]:ui-bg-gray-700 data-[state=checked]:ui-border-gray-700",
        "data-[state=checked]:ui-text-white",
        "dark:data-[state=checked]:ui-bg-indigo-500 dark:data-[state=checked]:ui-border-indigo-500",

        // Indeterminate state - dark background with white minus
        "data-[state=indeterminate]:ui-bg-gray-700 data-[state=indeterminate]:ui-border-gray-700",
        "data-[state=indeterminate]:ui-text-white",
        "dark:data-[state=indeterminate]:ui-bg-indigo-500 dark:data-[state=indeterminate]:ui-border-indigo-500",

        // Disabled state
        "disabled:ui-cursor-not-allowed disabled:ui-opacity-50",

        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="ui-grid ui-place-content-center ui-text-white ui-transition-none">
        {indeterminate ? (
          <Minus
            weight="bold"
            className={cn(
              size === "sm" ? "ui-w-3 ui-h-3" : "ui-w-3.5 ui-h-3.5"
            )}
          />
        ) : (
          <Check
            weight="bold"
            className={cn(
              size === "sm" ? "ui-w-3 ui-h-3" : "ui-w-3.5 ui-h-3.5"
            )}
          />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

CheckboxV2.displayName = "CheckboxV2";

export { CheckboxV2, checkboxVariants };
